import { useCallback, useState } from "react";

const DIGIO_SDK_URL_PRODUCTION = "https://app.digio.in/sdk/v11/digio.js";
const DIGIO_SDK_URL_SANDBOX = "https://ext-gateway.digio.in/sdk/v11/digio.js";

function getDigioSdkUrl(isSandbox) {
  return isSandbox ? DIGIO_SDK_URL_SANDBOX : DIGIO_SDK_URL_PRODUCTION;
}

function isDigioConstructor(value) {
  return typeof value === "function";
}

function clearDigioSdk() {
  document.querySelectorAll('script[data-digio-sdk="true"]').forEach((el) => {
    el.remove();
  });
  if (typeof window !== "undefined") {
    delete window.Digio;
  }
}

function waitForDigioCtor(maxAttempts = 40, intervalMs = 50) {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const tick = () => {
      if (isDigioConstructor(window.Digio)) {
        resolve(window.Digio);
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        reject(new Error("Digio SDK loaded but constructor is unavailable"));
        return;
      }

      setTimeout(tick, intervalMs);
    };

    tick();
  });
}

const loadDigioScript = (isSandbox) => {
  const sdkUrl = getDigioSdkUrl(isSandbox);

  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Digio SDK requires a browser environment"));
      return;
    }

    if (isDigioConstructor(window.Digio)) {
      const existing = document.querySelector(
        `script[data-digio-sdk="true"][src="${sdkUrl}"]`,
      );
      if (existing) {
        resolve(window.Digio);
        return;
      }
    }

    clearDigioSdk();

    const script = document.createElement("script");
    script.src = sdkUrl;
    script.async = true;
    script.dataset.digioSdk = "true";
    script.onload = () => {
      waitForDigioCtor()
        .then(resolve)
        .catch(reject);
    };
    script.onerror = () => reject(new Error("Failed to load Digio SDK"));
    document.head.appendChild(script);
  });
};

/** User closed Digio without finishing — not an error. */
function isDigioUserCancelled(response) {
  if (!response) return true;

  const code = response.error_code ?? response.code ?? response.status;
  const msg = String(response.message || response.error_message || "").toLowerCase();
  const codeStr = String(code).toUpperCase();

  if (code === -1000 || code === "-1000") return true;
  if (codeStr === "CANCEL" || codeStr === "CANCELLED") return true;
  if (/cancel|cancelled|canceled|closed|dismiss|abort/.test(msg)) return true;

  return false;
}

function resolveAccessTokenId({ accessTokenId, accessToken }) {
  if (accessTokenId) return String(accessTokenId).trim();
  if (!accessToken) return null;
  if (typeof accessToken === "string") return accessToken.trim() || null;
  if (typeof accessToken === "object" && accessToken.id) {
    return String(accessToken.id).trim() || null;
  }
  return null;
}

export function useDigio() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startDigioKyc = useCallback(
    async ({
      kycId,
      customerIdentifier,
      accessTokenId,
      accessToken,
      isSandbox = true,
    }) => {
      setLoading(true);
      setError(null);

      try {
        const DigioCtor = await loadDigioScript(isSandbox);

        if (!isDigioConstructor(DigioCtor)) {
          throw new Error("Digio SDK is not available");
        }

        const tokenId = resolveAccessTokenId({ accessTokenId, accessToken });

        const result = await new Promise((resolve, reject) => {
          let settled = false;

          const finish = (fn) => {
            if (settled) return;
            settled = true;
            fn();
          };

          const digio = new DigioCtor({
            environment: isSandbox ? "sandbox" : "production",
            is_iframe: true,
            callback: (response) => {
              setTimeout(() => {
                try {
                  if (!response) {
                    finish(() => resolve({ cancelled: true }));
                    return;
                  }

                  if (response.error_code || response.error) {
                    if (isDigioUserCancelled(response)) {
                      finish(() => resolve({ cancelled: true }));
                      return;
                    }
                    finish(() =>
                      reject(
                        new Error(
                          response.message ||
                            response.error_message ||
                            response.error_code ||
                            "Digio KYC failed",
                        ),
                      ),
                    );
                    return;
                  }

                  finish(() => resolve({ cancelled: false, response }));
                } catch (callbackErr) {
                  finish(() => reject(callbackErr));
                }
              }, 0);
            },
          });

          digio.init();
          if (tokenId) {
            digio.submit(kycId, customerIdentifier, tokenId);
          } else {
            digio.submit(kycId, customerIdentifier);
          }
        });

        return result;
      } catch (err) {
        setError(err?.message || "Digio verification failed");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { startDigioKyc, loading, error };
}
